import React, { useState, useMemo } from 'react';
import { Table } from 'antd';

/**
 * Componente de Grid customizado usando Ant Design Table
 * @param {Array} dataSource - Dados da tabela
 * @param {Array} columns - Colunas da tabela
 * @param {function} onRow - Função para eventos de linha
 * @param {boolean} loading - Se a tabela está carregando
 * @param {object} pagination - Configurações de paginação
 * @param {object} rowSelection - Configurações de seleção de linhas
 * @param {string} size - Tamanho da tabela (default, middle, small)
 * @param {boolean} bordered - Se a tabela tem bordas
 * @param {function} onChange - Função para mudanças (paginação, filtros, ordenação)
 */
const SL_Grid = ({ 
  dataSource = [], 
  columns = [], 
  onRow = null,
  loading = false,
  pagination = true,
  rowSelection = null,
  size = 'middle',
  bordered = false,
  onChange = null,
  rowKey = 'id',
  ...props 
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Configuração padrão de paginação
  const defaultPagination = pagination === true ? {
    current: currentPage,
    pageSize: pageSize,
    showSizeChanger: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} itens`,
    pageSizeOptions: ['10', '20', '50', '100'],
    onChange: (page, size) => {
      setCurrentPage(page);
      setPageSize(size);
    },
  } : pagination;

  // Handler para mudanças na tabela
  const handleTableChange = (newPagination, filters, sorter) => {
    if (newPagination) {
      setCurrentPage(newPagination.current);
      setPageSize(newPagination.pageSize);
    }
    
    if (onChange) {
      onChange(newPagination, filters, sorter);
    }
  };

  // Calcula a largura mínima total das colunas para habilitar scroll horizontal
  // quando necessário, respeitando a largura da tela
  const scrollConfig = useMemo(() => {
    // Largura padrão para colunas sem largura definida
    const DEFAULT_COLUMN_WIDTH = 150;
    
    // Calcula a soma das larguras das colunas
    const totalWidth = columns.reduce((sum, column) => {
      // Se a coluna tem largura definida, usa ela
      if (column.width) {
        return sum + column.width;
      }
      // Se não tem largura definida, usa a largura padrão
      return sum + DEFAULT_COLUMN_WIDTH;
    }, 0);

    // Retorna a largura mínima calculada
    // A tabela terá scroll horizontal quando o conteúdo for maior que a largura disponível
    return { x: totalWidth };
  }, [columns]);

  return (
    <div 
      style={{ 
        width: '100%', 
        maxWidth: '100%', 
        overflowX: 'auto', 
        overflowY: 'visible',
        boxSizing: 'border-box'
      }}
    >
      <Table
        dataSource={dataSource}
        columns={columns}
        onRow={onRow}
        loading={loading}
        pagination={defaultPagination}
        rowSelection={rowSelection}
        size={size}
        bordered={bordered}
        onChange={handleTableChange}
        rowKey={rowKey}
        scroll={scrollConfig}
        {...props}
      />
    </div>
  );
};

export default SL_Grid;



